import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import WidgetFactory from './widgets/WidgetFactory';
import useStore from '../store/useStore';
import { dashboardsAPI } from '../services/api';
import { useDashboardStale } from '../hooks/useDashboardStale';

const Dashboard = () => {
  const navigate = useNavigate();

  // Zustand selectors
  const currentDashboard = useStore((state) => state.currentDashboard);
  const user = useStore((state) => state.user);
  const isEditMode = useStore((state) => state.isEditMode);
  const setEditMode = useStore((state) => state.setEditMode);
  const updateDashboard = useStore((state) => state.updateDashboard);
  const isStale = useDashboardStale();

  // Helper to determine initial breakpoint from window width
  const getInitialBreakpoint = () => {
    if (typeof window === 'undefined') return 'lg';
    const width = window.innerWidth;
    if (width >= 1920) return 'xl';
    if (width >= 1200) return 'lg';
    if (width >= 996) return 'md';
    if (width >= 768) return 'sm';
    return 'xs';
  };

  // Local state
  const [layouts, setLayouts] = useState({ xl: [], lg: [], md: [], sm: [], xs: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [currentBreakpoint, setCurrentBreakpoint] = useState(getInitialBreakpoint());
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [renderedDashboardId, setRenderedDashboardId] = useState(currentDashboard?.id);
  const [cachedWidgets, setCachedWidgets] = useState(currentDashboard?.widgets || []);
  const [overlappingWidgets, setOverlappingWidgets] = useState(new Set());
  const [gridOptions, setGridOptions] = useState({
    preventCollision: false,
    allowOverlap: true,
    compactType: null, // null, 'vertical', or 'horizontal'
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isCalculatingRef = useRef(false);
  const currentDashboardIdRef = useRef(currentDashboard?.id);

  // Calculate row height based on window width
  const getRowHeight = () => {
    if (windowWidth < 768) return 60;
    if (windowWidth < 996) return 70;
    if (windowWidth < 1200) return 80;
    if (windowWidth < 1920) return 90;
    return 100;
  };

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fullscreen handlers
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Error entering fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Error exiting fullscreen:', err);
      });
    }
  };

  // Listen for fullscreen changes (in case user exits with ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcut (F key) to toggle fullscreen
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'f' || e.key === 'F') {
        // Don't trigger if user is typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  // Convert widget positions to React Grid Layout format
  useEffect(() => {
    const dashboardId = currentDashboard?.id;

    // If dashboard ID changed, reset the calculation flag
    if (currentDashboardIdRef.current !== dashboardId) {
      isCalculatingRef.current = false;
      currentDashboardIdRef.current = dashboardId;
      setIsLayoutReady(false);
      // DON'T clear renderedDashboardId - keep showing old dashboard until new one is ready
    }

    // Prevent duplicate calculations for the same dashboard
    if (isCalculatingRef.current) {
      return;
    }

    // Mark that we're calculating
    isCalculatingRef.current = true;

    setIsLayoutReady(false);

    if (!currentDashboard?.widgets) {
      setLayouts({ xl: [], lg: [], md: [], sm: [], xs: [] });
      setRenderedDashboardId(dashboardId);
      setCachedWidgets([]);
      setIsLayoutReady(true);
      isCalculatingRef.current = false;
      return;
    }

    const breakpointLayouts = { xl: [], lg: [], md: [], sm: [], xs: [] };

    currentDashboard.widgets.forEach((widget) => {
      const pos = widget.position || {};
      const hasBreakpoints = pos.xl !== undefined || pos.lg !== undefined;

      ['xl', 'lg', 'md', 'sm', 'xs'].forEach((breakpoint) => {
        const bpPos = hasBreakpoints
          ? (pos[breakpoint] || { x: 0, y: 0, w: 4, h: 3 })
          : { x: pos.x || 0, y: pos.y || 0, w: pos.w || 4, h: pos.h || 3 };

        let constraints = { minW: 2, minH: 2 };
        let width = bpPos.w || 4;
        let height = bpPos.h || 3;

        // Widget type-specific constraints
        if (widget.widget_type === 'gauge') {
          if (breakpoint === 'xl') {
            constraints = { minW: 2, minH: 2, maxW: 8, maxH: 4 };
          } else if (breakpoint === 'lg') {
            constraints = { minW: 1, minH: 2, maxW: 6, maxH: 2 };
          } else if (breakpoint === 'md') {
            constraints = { minW: 3, minH: 2, maxW: 8, maxH: 2 };
          } else if (breakpoint === 'sm') {
            constraints = { minW: 4, minH: 3, maxH: 3 };
            width = Math.max(width, 4);
            height = Math.max(height, 3);
          } else {
            constraints = { minW: 6, minH: 3, maxH: 3 };
            width = Math.max(width, 6);
            height = Math.max(height, 3);
          }
        } else if (widget.widget_type === 'count') {
          if (breakpoint === 'xl') {
            constraints = { minW: 2, minH: 2, maxW: 8 };
          } else if (breakpoint === 'lg') {
            constraints = { minW: 1, minH: 2, maxW: 6 };
          } else if (breakpoint === 'md') {
            constraints = { minW: 3, minH: 2, maxW: 8 };
          } else if (breakpoint === 'sm' || breakpoint === 'xs') {
            constraints = { minW: 6, minH: 2 };
            width = Math.max(width, 6);
          }
        }

        breakpointLayouts[breakpoint].push({
          i: widget.id.toString(),
          x: bpPos.x || 0,
          y: bpPos.y || 0,
          w: width,
          h: height,
          ...constraints,
        });
      });
    });

    setLayouts(breakpointLayouts);

    // Delay rendering slightly to ensure layout is fully calculated
    // This prevents the flash of widgets in wrong positions
    const timer = setTimeout(() => {
      setRenderedDashboardId(dashboardId);
      setCachedWidgets(currentDashboard?.widgets || []);
      setIsLayoutReady(true);
      isCalculatingRef.current = false; // Mark calculation as complete
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [currentDashboard?.id]);

  // Detect overlapping widgets
  const detectOverlaps = (layout) => {
    if (!layout || layout.length === 0) return new Set();

    const overlaps = new Set();

    for (let i = 0; i < layout.length; i++) {
      for (let j = i + 1; j < layout.length; j++) {
        const a = layout[i];
        const b = layout[j];

        // Check if widgets overlap
        if (!(a.x + a.w <= b.x || b.x + b.w <= a.x ||
              a.y + a.h <= b.y || b.y + b.h <= a.y)) {
          overlaps.add(a.i);
          overlaps.add(b.i);
        }
      }
    }

    return overlaps;
  };

  // Handle layout changes in edit mode
  const handleLayoutChange = (currentLayout, allLayouts) => {
    if (!isEditMode || !currentDashboard) return;
    // Only update layouts on drag/resize stop, not during drag
  };

  // Update layout state after drag/resize stops
  const handleDragStop = (layout) => {
    // Update the layouts state for the current breakpoint with the final positions
    setLayouts(prevLayouts => ({
      ...prevLayouts,
      [currentBreakpoint]: layout
    }));

    // Detect overlaps
    const overlaps = detectOverlaps(layout);
    setOverlappingWidgets(overlaps);
  };

  const handleResizeStop = (layout) => {
    // Update the layouts state for the current breakpoint with the final positions
    setLayouts(prevLayouts => ({
      ...prevLayouts,
      [currentBreakpoint]: layout
    }));

    // Detect overlaps
    const overlaps = detectOverlaps(layout);
    setOverlappingWidgets(overlaps);
  };

  // Handle breakpoint changes
  const handleBreakpointChange = (breakpoint) => {
    setCurrentBreakpoint(breakpoint);

    // Update overlaps for new breakpoint
    if (isEditMode && layouts[breakpoint]) {
      const overlaps = detectOverlaps(layouts[breakpoint]);
      setOverlappingWidgets(overlaps);
    }
  };

  // Save layout changes
  const saveLayout = async () => {
    if (!currentDashboard || !isEditMode) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      const updatedWidgets = currentDashboard.widgets.map((widget) => {
        const widgetId = widget.id.toString();
        const position = {};

        ['xl', 'lg', 'md', 'sm', 'xs'].forEach((breakpoint) => {
          const layout = layouts[breakpoint]?.find((l) => l.i === widgetId);
          if (layout) {
            position[breakpoint] = {
              x: Math.round(layout.x),
              y: Math.round(layout.y),
              w: Math.round(layout.w),
              h: Math.round(layout.h),
            };
          }
        });

        return {
          ...widget,
          position,
        };
      });

      const response = await dashboardsAPI.updateWidgetPositions(
        currentDashboard.id,
        updatedWidgets
      );

      if (response.success) {
        updateDashboard(currentDashboard.id, { widgets: updatedWidgets });
        setSaveMessage('Layout saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        console.error('[Dashboard] Save failed:', response.error || response);
        setSaveMessage('Failed to save layout: ' + (response.error || 'Unknown error'));
        setTimeout(() => setSaveMessage(''), 5000);
      }
    } catch (error) {
      console.error('[Dashboard] Error saving layout:', error);
      setSaveMessage('Failed to save layout: ' + error.message);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle edit mode
  const handleToggleEditMode = async () => {
    if (isEditMode) {
      await saveLayout();
      setOverlappingWidgets(new Set()); // Clear overlaps when exiting edit mode
    } else {
      // Detect initial overlaps when entering edit mode
      if (layouts[currentBreakpoint]) {
        const overlaps = detectOverlaps(layouts[currentBreakpoint]);
        setOverlappingWidgets(overlaps);
      }
    }
    setEditMode(!isEditMode);
  };

  if (!currentDashboard && !renderedDashboardId) {
    return (
      <div className="dashboard-container">
        <div style={{ textAlign: 'center', color: '#b0b0b0', padding: '4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
          <h2>No Dashboard Selected</h2>
          <p>Please select a dashboard from the navigation or create a new one.</p>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <button
              onClick={() => navigate('/settings')}
              className="btn-primary"
              style={{ marginTop: '1rem' }}
            >
              Go to Settings
            </button>
          )}
        </div>
      </div>
    );
  }

  const isMobile = windowWidth < 768;
  const shouldShowGrid = isLayoutReady && renderedDashboardId === currentDashboard?.id;
  const isLoadingNewDashboard = (currentDashboard?.id && renderedDashboardId && renderedDashboardId !== currentDashboard?.id) || (!currentDashboard && renderedDashboardId);

  return (
    <div className="dashboard-container">
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="fullscreen-exit-btn"
          title="Exit Fullscreen (Press F or ESC)"
        >
          Exit Fullscreen
        </button>
      )}
      {!isFullscreen && isStale && currentDashboard && (
        <div style={{
          backgroundColor: 'rgba(255, 152, 0, 0.9)',
          color: '#000',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontWeight: '600',
          fontSize: '0.95rem',
          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
        }}>
          <span style={{ fontSize: '1.5rem' }}>⚠️</span>
          <span>Dashboard data is stale</span>
        </div>
      )}
      {!isFullscreen && (
        <div className="dashboard-header" style={{
        padding: isMobile ? '0.5rem 0' : '1rem 0',
        marginBottom: isMobile ? '0.5rem' : '1rem',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? '0.5rem' : '0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 style={{ color: '#4a9eff', fontSize: isMobile ? '1.5rem' : '2rem' }}>
            {currentDashboard?.name || 'Loading...'}
          </h2>
          <span style={{
            fontSize: '0.75rem',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            backgroundColor: 'rgba(74, 158, 255, 0.2)',
            color: '#4a9eff',
            fontWeight: '600',
            textTransform: 'uppercase'
          }}>
            {currentBreakpoint === 'xl' ? 'Extra Large' :
             currentBreakpoint === 'lg' ? 'Desktop' :
             currentBreakpoint === 'md' ? 'Tablet' :
             currentBreakpoint === 'sm' ? 'Small' : 'Mobile'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          {saveMessage && (
            <span style={{
              color: saveMessage.includes('Failed') ? '#ff6b6b' : '#51cf66',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              fontWeight: '500'
            }}>
              {saveMessage}
            </span>
          )}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <button
              onClick={handleToggleEditMode}
              className={isEditMode ? 'btn-secondary' : 'btn-primary'}
              disabled={isSaving}
              style={{ fontSize: isMobile ? '0.8rem' : '1rem', padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem' }}
            >
              {isSaving ? 'Saving...' : isEditMode ? 'Save Layout' : 'Edit Layout'}
            </button>
          )}
          <button
            onClick={toggleFullscreen}
            className="btn-secondary"
            title="Toggle Fullscreen (Press F)"
            style={{ fontSize: isMobile ? '0.8rem' : '1rem', padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem' }}
          >
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>
        </div>
      )}

      {!isFullscreen && isEditMode && (
        <div className="grid-options-panel">
          {!gridOptions.allowOverlap && (
            <div className="grid-options-section">
              <label className="grid-option-label">Compaction:</label>
              <div className="grid-option-buttons">
                <button
                  onClick={() => setGridOptions({ ...gridOptions, compactType: null })}
                  className={`grid-option-btn ${gridOptions.compactType === null ? 'active' : ''}`}
                >
                  None
                </button>
                <button
                  onClick={() => setGridOptions({ ...gridOptions, compactType: 'vertical' })}
                  className={`grid-option-btn ${gridOptions.compactType === 'vertical' ? 'active' : ''}`}
                >
                  Vertical
                </button>
                <button
                  onClick={() => setGridOptions({ ...gridOptions, compactType: 'horizontal' })}
                  className={`grid-option-btn ${gridOptions.compactType === 'horizontal' ? 'active' : ''}`}
                >
                  Horizontal
                </button>
              </div>
            </div>
          )}

          <div className="grid-options-section">
            <label className="grid-option-checkbox">
              <input
                type="checkbox"
                checked={gridOptions.preventCollision}
                onChange={(e) => setGridOptions({
                  ...gridOptions,
                  preventCollision: e.target.checked,
                  allowOverlap: e.target.checked ? false : gridOptions.allowOverlap
                })}
              />
              <span>Prevent Collision</span>
            </label>
          </div>

          {!gridOptions.preventCollision && (
            <div className="grid-options-section">
              <label className="grid-option-checkbox">
                <input
                  type="checkbox"
                  checked={gridOptions.allowOverlap}
                  onChange={(e) => setGridOptions({
                    ...gridOptions,
                    allowOverlap: e.target.checked,
                    compactType: e.target.checked ? null : gridOptions.compactType
                  })}
                />
                <span>Allow Overlap</span>
              </label>
            </div>
          )}
        </div>
      )}

      {shouldShowGrid || renderedDashboardId ? (
        <div style={{ position: 'relative' }}>
          {isLoadingNewDashboard && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              backdropFilter: 'blur(2px)'
            }}>
              <div className="loading-spinner"></div>
            </div>
          )}
          <ResponsiveGridLayout
            key={renderedDashboardId}
            className="layout"
            layouts={layouts}
            breakpoints={{ xl: 1920, lg: 1200, md: 996, sm: 768, xs: 480 }}
            cols={{ xl: 18, lg: 12, md: 12, sm: 12, xs: 12 }}
            rowHeight={getRowHeight()}
            width={isMobile ? windowWidth - 24 : windowWidth - 64}
            isDraggable={isEditMode}
            isResizable={isEditMode}
            onLayoutChange={handleLayoutChange}
            onBreakpointChange={handleBreakpointChange}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            draggableHandle=".widget-header"
            compactType={gridOptions.compactType}
            allowOverlap={gridOptions.allowOverlap}
            preventCollision={gridOptions.preventCollision}
            margin={isMobile ? [8, 8] : [10, 10]}
            containerPadding={isMobile ? [8, 8] : [0, 0]}
          >
            {cachedWidgets && cachedWidgets.map((widget) => {
              const isOverlapping = overlappingWidgets.has(widget.id.toString());
              return (
                <div
                  key={widget.id.toString()}
                  className={isEditMode && isOverlapping ? 'overlapping-widget' : ''}
                >
                  <WidgetFactory widget={widget} />
                </div>
              );
            })}
          </ResponsiveGridLayout>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          <div className="loading-spinner"></div>
        </div>
      )}

      {shouldShowGrid && cachedWidgets && cachedWidgets.length === 0 && (
        <div style={{ textAlign: 'center', color: '#b0b0b0', padding: '4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
          <h3>No Widgets Added</h3>
          <p>Go to Settings to add widgets to this dashboard.</p>
          <button
            onClick={() => navigate('/settings')}
            className="btn-primary"
            style={{ marginTop: '1rem' }}
          >
            Go to Settings
          </button>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
